<template>
    <div>
        <div class="tile-content">
            <div class="grid-content">
                <div class="grid-item grid-item-half grid-item-break-half">
                    <v-select
                        label="Select your Pricing Formula"
                        :options="pricingFormulaList"
                        :preselect-first="true"
                        :value="planPricing.formula"
                        @input="updateFormula"/>
                </div>
            </div>
        </div>
        <div class="tile-content" v-if="hasPricing">
            <div class="grid-content" >
                <div class="grid-item grid-item-quarter grid-item-break-half">
                    <r-input
                        :left-label="currencySign"
                        :label="pricingInputLabel"
                        :value="planPricing.price"
                        @input="updatePrice"
                        :validate="validate.pricing.price"
                    />
                </div>
            </div>
        </div>
        <div class="tile-content" v-if="hasBracketPricing">
            <v-error-messages :has-frontend-errors="validate.pricing.$error" class="stack-l">
                <template v-for="(validation, index) in validate.pricing.brackets.$model">
                    <li class="list-item" v-if="!validation.price">Plan price on bracket {{index + 1}} is required and must be a number</li>
                    <li class="list-item" v-if="!validation.maxQuantity && validate.pricing.brackets[index].maxQuantity !== undefined">Plan quantity on bracket {{index + 1}} is required and must be a number</li>
                    <li class="list-item" v-if="validate.pricing.brackets[index].maxQuantity !== undefined && !validate.pricing.brackets[index].maxQuantity.validQuantity">Plan quantity on bracket {{index + 1}} is bellow the previous bracket quantity</li>
                </template>
            </v-error-messages>
            <div class="grid-content" >
                <div class="grid-item grid-item-full">
                    <div v-for="(bracket, index) in planPricing.brackets" :key="index">
                        <div class="grid-fitted grid-unstackable stack-m align-items-center">
                            <div class="grid-item inline-s">
                                <div class="field">
                                    <div class="field-group">
                                        <div class="field-addon field-addon-clear no-flex">Charge</div>
                                        <r-input
                                            :left-label="currencySign"
                                            :value="bracket.price"
                                            @input="updateBracketPrice(index, $event)"
                                            :validate="validate.pricing.brackets[index].price"/>
                                        <div v-if="planPricing.formula === 'tiered' && index !== 0" class="field-addon field-addon-clear no-flex">{{bracketItemStyleAndMessage.nextMessage}}</div>
                                        <div v-else class="field-addon field-addon-clear no-flex">{{bracketItemStyleAndMessage.message}}</div>
                                        <r-input :value="previousMaxQuantity(index)" disabled/>
                                        <template v-if="index === planPricing.brackets.length - 1 && index !== 0">
                                            <div class="field-addon field-addon-clear text-left" :style="bracketItemStyleAndMessage.style">or more</div>
                                        </template>
                                        <template v-else>
                                            <div class="field-addon field-addon-clear no-flex">to</div>
                                            <r-input
                                                @input="updateBracketMaxQuantity(index, $event)"
                                                :value="bracket.maxQuantity"
                                                :validate="validate.pricing.brackets[index].maxQuantity"/>
                                        </template>
                                    </div>
                                </div>
                            </div>
                            <template v-if="index !== planPricing.brackets.length - 1 || index === 1">
                                <div class="grid-item no-flex">
                                    <v-icon-button disabled type="basic"><v-icon icon=""/></v-icon-button>
                                </div>
                            </template>
                            <template v-else>
                                <div class="grid-item no-flex">
                                    <v-icon-button @click="deleteBracketItem(index)" type="basic"><v-icon icon="trash"/></v-icon-button>
                                </div>
                            </template>
                        </div>
                    </div>
                    <r-button type="link" @click="addNewBracket">Add another price bracket</r-button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {
        vErrorMessages, vIcon, vIconButton, vSelect,
    } from '@/components/form';
    import PlanFormulaModel, {PricingBracketModel} from '@/common/models/plan-formula-model';

    export default {
        name: 'plan-pricing',
        components: {
            vSelect,
            vIcon,
            vIconButton,
            vErrorMessages,
        },
        props: {
            planPricing: {
                type: Object,
            },
            currencySign: {
                type: String,
            },
            validate: {
                type: Object,
                default: null,
            },
            productUnit: {
                type: String,
            },
        },
        data() {
            return {
                pricingFormulaList: [
                    {label: 'Fixed Fee', value: PlanFormulaModel.Formulas.fixedFee},
                    {label: 'Flat Rate', value: PlanFormulaModel.Formulas.flatRate},
                    {label: 'Stairstep', value: PlanFormulaModel.Formulas.stairstep},
                    {label: 'Tiered', value: PlanFormulaModel.Formulas.tiered},
                    {label: 'Volume', value: PlanFormulaModel.Formulas.volume},
                ],
            };
        },
        computed: {
            hasPricing() {
                return this.planPricing.formula === PlanFormulaModel.Formulas.fixedFee
                    || this.planPricing.formula === PlanFormulaModel.Formulas.flatRate;
            },
            hasBracketPricing() {
                return this.planPricing.formula === PlanFormulaModel.Formulas.stairstep
                    || this.planPricing.formula === PlanFormulaModel.Formulas.tiered
                    || this.planPricing.formula === PlanFormulaModel.Formulas.volume;
            },
            pricingInputLabel() {
                if (this.planPricing.formula === PlanFormulaModel.Formulas.fixedFee) {
                    return 'Price';
                }
                return `Price per ${this.productUnit}`;
            },
            bracketItemStyleAndMessage() {
                if (this.planPricing.formula === PlanFormulaModel.Formulas.stairstep) {
                    return {
                        message: `in total (not per ${this.productUnit}) when quantity is`,
                        style: {marginRight: '10px'},
                    };
                } if (this.planPricing.formula === PlanFormulaModel.Formulas.tiered) {
                    return {
                        message: `per ${this.productUnit} for the first`,
                        nextMessage: `per ${this.productUnit} for the next`,
                        style: {marginRight: '12px'},
                    };
                } if (this.planPricing.formula === PlanFormulaModel.Formulas.volume) {
                    return {
                        message: `per ${this.productUnit} when the total quantity is`,
                        style: {marginRight: '12px'},
                    };
                }
                return {};
            },
        },
        methods: {
            addNewBracket() {
                const lastBracketItem = this.planPricing.brackets.length - 1;
                if (lastBracketItem > 0) {
                    this.updateBracketMaxQuantity(lastBracketItem, null);
                }
                this.planPricing.brackets.push(new PricingBracketModel({price: null, maxQuantity: null}));
            },
            deleteBracketItem(index) {
                this.planPricing.brackets.splice(index, 1);
                if (index > 1) {
                    this.updateBracketMaxQuantity(index - 1, null);
                }
            },
            updateFormula(formula) {
                this.updatePrice(0);
                this.$emit('input', {
                    planPricing: new PlanFormulaModel({...this.planPricing, formula}),
                });
            },
            updatePrice(price) {
                this.$emit('input', {
                    planPricing: new PlanFormulaModel({...this.planPricing, price}),
                });
            },
            previousMaxQuantity(index) {
                if (index > 0) {
                    const followUpNumber = Number(this.planPricing.brackets[index - 1].maxQuantity) + 1;
                    if (isNaN(followUpNumber) || followUpNumber === 1) {
                        return null;
                    }
                    return followUpNumber;
                }
                return 1;
            },
            updateBracketPrice(index, price) {
                this.$set(this.planPricing.brackets, index, new PricingBracketModel({
                    ...this.planPricing.brackets[index],
                    price,
                }));
                this.$emit('input', {planPricing: this.planPricing});
            },
            updateBracketMaxQuantity(index, maxQuantity) {
                this.$set(this.planPricing.brackets, index, new PricingBracketModel({
                    ...this.planPricing.brackets[index],
                    maxQuantity: Number(maxQuantity),
                }));
                this.$emit('input', {planPricing: this.planPricing});
            },
        },
        watch: {
            'planPricing.formula': function watchPlanPricingFormula() {
                this.planPricing.price = 0;
                if (this.hasBracketPricing) {
                    this.planPricing.brackets = [
                        new PricingBracketModel({price: null, maxQuantity: null}),
                        new PricingBracketModel({price: null, maxQuantity: null}),
                    ];
                }
            },
        },
    };
</script>
